import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import type { AddressPayload } from '~/types/global'
import { toast } from '~/utils'
import type { RecourseCollectionResponse } from '../queries/listWithFilters'

type CreateRecoursePayload = {
  contact: string
  qualifications: string[]
  specialities?: string[]
  jobTitle?: string
  address?: AddressPayload
  locationRadius?: number
  applicantWantedCount: number
}

type CreateMultipleRecoursesPayload = {
  contacts: string[]
  qualifications: string[]
  specialities?: string[]
  jobTitle?: string
  address?: AddressPayload
  locationRadius?: number
  applicantWantedCount: number
}

export const useCreateRecourseMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    RecourseCollectionResponse,
    ApiResponseError,
    CreateRecoursePayload
  >({
    mutationFn: async ({
      contact,
      qualifications,
      specialities,
      jobTitle,
      address,
      locationRadius,
      applicantWantedCount
    }) => {
      const payload: any = {
        contact,
        qualification: `/v1/taxonomies/qualifications/${qualifications[0]}`,
        applicantWantedCount: applicantWantedCount ?? 0
      }

      if (specialities?.length) {
        payload.specialities = specialities.map(
          (s) => `/v1/taxonomies/specialities/${s}`
        )
      }

      if (jobTitle) {
        payload.descriptifPoste = jobTitle
      }

      if (address) {
        payload.address = address
      }

      if (locationRadius) {
        payload.locationRadius = locationRadius
      }

      return fetchApiWithToken<RecourseCollectionResponse>({
        method: 'post',
        url: '/v1/recourses',
        data: payload
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recourses'] })
      toast(
        'Recours créé',
        'Le recours a été créé avec succès',
        ToastType.Success
      )
    },
    onError: (error) => {
      toast(
        'Erreur lors de la création',
        error.response?.message ||
          'Une erreur est survenue lors de la création du recours',
        ToastType.Danger
      )
    }
  })
}

export const useCreateMultipleRecoursesMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ApiResponseError, CreateMultipleRecoursesPayload>({
    mutationFn: async ({
      contacts,
      qualifications,
      specialities,
      jobTitle,
      address,
      locationRadius,
      applicantWantedCount
    }) => {
      const requests = contacts.flatMap((contact) =>
        qualifications.map((qualification) => {
          const payload: Record<string, unknown> = {
            contact,
            qualification: `/v1/taxonomies/qualifications/${qualification}`,
            applicantWantedCount: applicantWantedCount ?? 0
          }
          if (specialities?.length) {
            payload.specialities = specialities.map(
              (s) => `/v1/taxonomies/specialities/${s}`
            )
          }
          if (jobTitle) payload.descriptifPoste = jobTitle
          if (address) payload.address = address
          if (locationRadius) payload.locationRadius = locationRadius
          return fetchApiWithToken({
            method: 'post',
            url: '/v1/recourses',
            data: payload
          })
        })
      )
      await Promise.all(requests)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recourses'] })
      toast(
        'Recours créés',
        'Les recours ont été créés avec succès',
        ToastType.Success
      )
    },
    onError: (error) => {
      toast(
        'Erreur lors de la création',
        error.response?.message ||
          'Une erreur est survenue lors de la création des recours',
        ToastType.Danger
      )
    }
  })
}
